import { FormEvent, useEffect, useState } from "react";

import { updateFormCategories } from "@/api/category";
import { createForm, editForm } from "@/api/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useFormModalStore } from "@/hooks/store/use-form-modal-store";

import CategoryMultiSelect from "../category-multi-select";

import { getCategoryIds } from "@/utils/category";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Modal from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";

const formTypes = [
  { value: "1", label: "خودکار" },
  { value: "2", label: "صرفا برای افزودن" }
];

const FormModal = () => {
  const queryClient = useQueryClient();

  const { isOpen, onClose, form } = useFormModalStore();
  const [selectedType, setSelectedType] = useState<string>(
    String(form?.type ?? 1)
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(
    () => getCategoryIds(form)
  );

  useEffect(() => {
    setSelectedType(String(form?.type ?? 1));
    setSelectedCategoryIds(getCategoryIds(form));
  }, [form, isOpen]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: FormData) => {
      const response = form ? await editForm(data) : await createForm(data);
      const targetId = form?.id ?? response?.data?.id;

      if (targetId && selectedCategoryIds.length) {
        await updateFormCategories(targetId, selectedCategoryIds);
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["forms"] });
      onClose();
    }
  });

  const titleText = form ? `ویرایش فرم ${form.name}` : "ساخت فرم جدید";
  const submitBtnText = form ? "ویرایش فرم" : "ساخت فرم جدید";

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    if (!form) {
      formData.append(
        "HtmlFormBody",
        `<div style="width:1000px; min-height:500px; background-color:#FFFFFF"></div>`
      );
    } else {
      formData.append("id", String(form.id));
      formData.append("HtmlFormBody", form.htmlFormBody);
    }

    mutate(formData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} description="" title={titleText}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="flex flex-col gap-y-1">
          <label htmlFor="" className="text-sm">
            نام
          </label>
          <Input
            type="text"
            name="name"
            defaultValue={form?.name ?? ""}
            placeholder="نام فرم"
            required
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <label htmlFor="" className="text-sm">
            توضیحات
          </label>
          <Textarea
            name="description"
            defaultValue={form?.description ?? ""}
            placeholder="توضیحات..."
            className="resize-none"
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <label htmlFor="form-type" className="text-sm">
            نوع فرم
          </label>
          <select
            id="form-type"
            name="type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus-within:border-primary focus-within:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            required
          >
            {formTypes.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <CategoryMultiSelect
          value={selectedCategoryIds}
          onChange={setSelectedCategoryIds}
          title="دسته بندی های فرم"
          description="یک یا چند دسته بندی برای فرم انتخاب کنید"
        />
        <Button type="submit" className="w-full" disabled={isPending}>
          {submitBtnText}
        </Button>
      </form>
    </Modal>
  );
};

export default FormModal;
